import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementContentSkeleton } from '../layout/ManagementPageSkeleton';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ServiceComboCard from './ServiceComboCard';
import ServiceComboEditorDialog from './ServiceComboEditorDialog';
import { useToast } from '../../context/ToastContext';
import ServiceComboService from '../../services/serviceComboService';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getLocalizedValue } from '../../constants/Languages';

const CLOSED_DIALOG = { open: false, mode: 'create', combo: null };

const errorKey = (error, fallback) => {
  const code = getApiErrorCode(error);
  if (code && [
    'InvalidServiceComboData',
    'ServiceComboNotFound',
    'PlanUpgradeRequired',
    'Forbidden',
  ].includes(code)) {
    return `serviceCombosManagement.apiErrors.${code}`;
  }

  return fallback;
};

const ServiceCombosPanel = ({
  storeId,
  services,
  supportedLanguages,
  currency,
  canWrite,
  isPro = true,
  onlinePaymentsReady = false,
  installmentMethodsAvailable = false,
}) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG);
  const [saving, setSaving] = useState(false);
  const [comboToDelete, setComboToDelete] = useState(null);
  const [statusComboId, setStatusComboId] = useState(null);

  const loadCombos = useCallback(async () => {
    if (!storeId) {
      setError('serviceCombosManagement.error.noStoreSelected');
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`service-combos-${storeId}`, async () => {
      try {
        setLoading(true);
        setError(null);
        setCombos(await ServiceComboService.getServiceCombos(storeId));
      } catch (loadError) {
        console.error('Failed to load service combos:', loadError);
        setError(errorKey(loadError, 'serviceCombosManagement.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId]);

  useEffect(() => {
    loadCombos();
  }, [loadCombos]);

  const handleSave = async (combo) => {
    const isEdit = dialogState.mode === 'edit' && dialogState.combo?.id;
    try {
      setSaving(true);
      if (isEdit) {
        await ServiceComboService.updateServiceCombo(storeId, dialogState.combo.id, combo);
      } else {
        await ServiceComboService.createServiceCombo(storeId, combo);
      }

      showSuccess(t(isEdit ? 'serviceCombosManagement.feedback.updated' : 'serviceCombosManagement.feedback.created'));
      setDialogState(CLOSED_DIALOG);
      await loadCombos();
    } catch (saveError) {
      console.error('Failed to save service combo:', saveError);
      showError(t(errorKey(saveError, isEdit ? 'serviceCombosManagement.error.update' : 'serviceCombosManagement.error.create')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!comboToDelete) {
      return;
    }

    try {
      setStatusComboId(comboToDelete.id);
      await ServiceComboService.deleteServiceCombo(storeId, comboToDelete.id);
      showSuccess(t('serviceCombosManagement.feedback.deleted'));
      await loadCombos();
    } catch (deleteError) {
      console.error('Failed to delete service combo:', deleteError);
      showError(t(errorKey(deleteError, 'serviceCombosManagement.error.delete')));
    } finally {
      setStatusComboId(null);
      setComboToDelete(null);
    }
  };

  if (loading) {
    return <ManagementContentSkeleton variant="cards" withAction={canWrite} />;
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{t(error)}</Alert>
        <Button variant="contained" onClick={loadCombos}>{t('common.retry')}</Button>
      </Box>
    );
  }

  return (
    <Box id="service-combos-content" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {canWrite ? (
        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mb: 4 }}>
          <PrimaryActionButton
            id="service-combos-add"
            startIcon={<AddIcon />}
            onClick={() => setDialogState({ open: true, mode: 'create', combo: null })}
            sx={{ width: { xs: '100%', sm: 'auto' }, px: 4, py: 1.5 }}
          >
            {t('serviceCombosManagement.addNew')}
          </PrimaryActionButton>
        </Box>
      ) : null}
      {combos.length === 0 ? (
        <Box id="service-combos-empty" sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>{t('serviceCombosManagement.noCombos')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('serviceCombosManagement.emptyHint')}</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {combos.map((combo) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={combo.id}>
              <ServiceComboCard
                combo={combo}
                services={services}
                currency={currency}
                loading={saving || statusComboId === combo.id}
                canEdit={canWrite}
                onEdit={() => setDialogState({ open: true, mode: 'edit', combo })}
                onDelete={() => setComboToDelete(combo)}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <ServiceComboEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        combo={dialogState.combo}
        services={services}
        supportedLanguages={supportedLanguages}
        currency={currency}
        storeId={storeId}
        isPro={isPro}
        onlinePaymentsReady={onlinePaymentsReady}
        installmentMethodsAvailable={installmentMethodsAvailable}
        saving={saving}
        onClose={() => setDialogState(CLOSED_DIALOG)}
        onSave={handleSave}
      />
      <ConfirmDialog
        open={Boolean(comboToDelete)}
        onClose={() => setComboToDelete(null)}
        onConfirm={handleDelete}
        title={t('serviceCombosManagement.delete.title')}
        message={t('serviceCombosManagement.delete.message', {
          name: comboToDelete
            ? getLocalizedValue(comboToDelete.name, i18n.language, undefined, t('serviceCombosManagement.card.unnamed'))
            : '',
        })}
        confirmText={t('serviceCombosManagement.delete.confirm')}
        cancelText={t('common.cancel')}
      />
    </Box>
  );
};

export default ServiceCombosPanel;
